// frontend/src/pages/EmailAds.jsx
import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import axios from "../utils/axios";

function EmailAds() {
  const [copied, setCopied] = useState(false);
  const [emailHTML, setEmailHTML] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch the email template from backend on component mount
  useEffect(() => {
    fetchEmailTemplate();
  }, []);

  const fetchEmailTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/email/template");
      if (response.data.success) {
        setEmailHTML(response.data.template);
      } else {
        setError("Failed to load email template");
      }
    } catch (err) {
      console.error("Error fetching template:", err);
      setError("Failed to load email template. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyHTML = async () => {
    try {
      await navigator.clipboard.writeText(emailHTML);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error("Failed to copy: ", error);
    }
  };

  const handleDownloadHTML = () => {
    const blob = new Blob([emailHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "webvenly-email-advertisement.html";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3">
            📧 Marketing Email Templates
          </h1>
          <p className="lead text-muted">
            Professional email advertisements ready to use for your WebVenly
            marketing campaigns
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            {/* Main Card */}
            <div className="card shadow-lg border-0">
              <div className="card-header bg-gradient-primary text-white">
                <h3 className="mb-0">
                  <i className="bi bi-envelope-fill me-2"></i>
                  WebVenly Email Advertisement
                </h3>
                <p className="mb-0 mt-2">
                  Complete HTML email template ready for your campaigns
                </p>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading email template...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger m-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                    <button
                      className="btn btn-sm btn-outline-danger ms-3"
                      onClick={fetchEmailTemplate}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Action Buttons */}
                    <div className="p-4 bg-light border-bottom">
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-success"
                          onClick={handleCopyHTML}
                        >
                          <i className="bi bi-clipboard me-2"></i>
                          {copied ? "Copied!" : "Copy HTML Code"}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleDownloadHTML}
                        >
                          <i className="bi bi-download me-2"></i>
                          Download HTML File
                        </button>
                        <a
                          href="mailto:?subject=WebVenly%20Demo&body=I'd%20like%20to%20learn%20more%20about%20WebVenly"
                          className="btn btn-outline-secondary"
                        >
                          <i className="bi bi-envelope me-2"></i>
                          Test in Email Client
                        </a>
                        <button
                          className="btn btn-outline-info"
                          onClick={fetchEmailTemplate}
                        >
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Refresh Template
                        </button>
                      </div>
                      <div className="mt-3">
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          <strong>How to use:</strong> Copy the HTML code and
                          paste it into your email marketing platform
                          (Mailchimp, Constant Contact, etc.) or download the
                          file to use locally.
                        </small>
                      </div>
                    </div>

                    {/* Email Preview */}
                    <div className="p-4">
                      <h5 className="mb-3">📱 Email Preview</h5>
                      <div
                        className="border rounded p-3"
                        style={{
                          maxHeight: "600px",
                          overflowY: "auto",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: emailHTML }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Usage Instructions - Only show when template is loaded */}
            {!loading && !error && (
              <div className="card mt-4 border-warning">
                <div className="card-header bg-warning bg-opacity-10">
                  <h4 className="mb-0 text-warning">
                    <i className="bi bi-lightbulb-fill me-2"></i>
                    Usage Instructions
                  </h4>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6>📧 For Email Marketing Platforms:</h6>
                      <ol className="small">
                        <li>Copy the HTML code above</li>
                        <li>Open your email platform (Mailchimp, etc.)</li>
                        <li>Choose "HTML" or "Code" editor</li>
                        <li>Paste the code</li>
                        <li>Update links to match your URLs</li>
                        <li>Test and send!</li>
                      </ol>
                    </div>
                    <div className="col-md-6">
                      <h6>📝 For Outlook/Manual Sending:</h6>
                      <ol className="small">
                        <li>Download the HTML file</li>
                        <li>Open in web browser</li>
                        <li>Copy the rendered email</li>
                        <li>Paste into Outlook as formatted text</li>
                        <li>Or use Outlook's HTML import feature</li>
                      </ol>
                    </div>
                  </div>

                  <div className="mt-3 p-3 bg-light rounded">
                    <h6>📈 Suggested Subject Lines:</h6>
                    <ul className="small mb-0">
                      <li>
                        "Stop Drowning in Paperwork - See How [Company] Saves
                        10+ Hours/Week"
                      </li>
                      <li>"Finally: CRP Software That Actually Works"</li>
                      <li>"Cut Your Paperwork in Half - Free Demo Inside"</li>
                      <li>"Transform Your CRP Operations in 30 Days"</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="text-center mt-4">
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <a href="/demo" className="btn btn-warning">
                  <i className="bi bi-calendar-check me-2"></i>
                  Book a Demo
                </a>
                <a href="/contact" className="btn btn-outline-primary">
                  <i className="bi bi-envelope me-2"></i>
                  Contact Us
                </a>
                <a href="/features" className="btn btn-outline-secondary">
                  <i className="bi bi-gear me-2"></i>
                  View Features
                </a>
                <a href="/email-marketing" className="btn btn-outline-info">
                  <i className="bi bi-send me-2"></i>
                  Send Emails
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EmailAds;
